import { type Accept, useDropzone } from 'react-dropzone';
import type { FormError } from './types';
import type { InputHTMLAttributes } from 'react';
import Button from '../common/Button';
import classnames from 'classnames';
import Error from './Error';
import Label from './Label';

interface FileDropZoneProps {
  accept?: Accept;
  error?: FormError;
  file: File | null;
  label: string;
  name?: string;
  handleDrop: (file: File | null) => void;
  testId?: string;
}
export const FileDropZone = ({
  accept = {
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  },
  error,
  file,
  label,
  name = 'file',
  handleDrop,
  testId = '',
}: FileDropZoneProps) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles[0]) {
        handleDrop(acceptedFiles[0]);
      }
    },
  });

  const inputProps = {
    className: classnames('form-control', { 'is-invalid': error }),
    'data-testid': testId,
    id: name,
    label,
    name,
    ...getInputProps(),
  } as InputHTMLAttributes<HTMLInputElement>;

  const acceptedFileTypes = Object.values(accept)
    .flat()
    .map((extension) => extension.replace('.', '').toUpperCase())
    .join(', ');

  return (
    <div className="form-group">
      <Label name={name}>{label}</Label>
      <div
        className={classnames(
          'border border-gray-dashed cursor-pointer mb-3 p-4 rounded text-center text-muted',
          file ? 'bg-light-green' : 'bg-input-bg'
        )}
        {...getRootProps()}
      >
        <input {...inputProps} />
        {isDragActive ? (
          <span>Drop the file here.</span>
        ) : file ? (
          <>
            <span className="text-truncate text-green">{file.name}</span>
            <span onClick={(event) => event.stopPropagation()}>
              <Button variant="default" onClick={() => handleDrop(null)}>
                x
              </Button>
            </span>
          </>
        ) : (
          <>
            <div className="text-dark mb-1">Drop a file here or click to browse.</div>
            <div className="text-0_9">Only one file of the following supported types: {acceptedFileTypes}</div>
          </>
        )}
        <Error>{error?.message}</Error>
      </div>
    </div>
  );
};

export default FileDropZone;
